import _ from 'lodash';

import {
  withoutReportBaselines,
  withoutReportComparison,
} from '../blockComparison';
import { withoutEntityDetailsPopups } from '../buildEntityInformationTableDefinition';
import { isRemindersTable } from '../buildRemindersDefinition';
import { withCurrentVisType } from '../visTypes';

// Leaves out absent optional fields, so blocks read from a gadget compare
// equal to the same block after an edit that changed nothing
export const withoutUndefined = <T extends object>(value: T): T =>
  _.omitBy(value, _.isUndefined) as T;

export const pickDateSettings = (dates: DateFiltered): DateFiltered =>
  withoutUndefined({
    dateField: dates.dateField,
    dateRange: dates.dateRange,
    relativeDateRange: dates.relativeDateRange,
    advancedRelativeDateRange: dates.advancedRelativeDateRange,
  });

// Report gadgets can only override the date field: the range always comes
// from the report (ReportGadgetDateScopeProvider). Report blocks and their
// Composite elements follow the same rule.
export const forContentType = ({
  dateSettings,
  contentType,
}: {
  dateSettings: DateFiltered | undefined;
  contentType: Studio.ContentType;
}): DateFiltered | undefined => {
  if (!dateSettings) {
    return undefined;
  }
  return contentType === 'report'
    ? { dateField: dateSettings.dateField }
    : pickDateSettings(dateSettings);
};

// A chart element without its popup and run-time filters (an entity details
// Table without its rows' popups), which only a dashboard has (as only
// dashboard gadgets do)
export const withoutElementDrillDowns = (
  element: Studio.CompositeElement,
): Studio.CompositeElement =>
  withoutUndefined({
    ..._.omit(element, 'drillDowns', 'reportDrillDownId', 'boardDrillDownId'),
    // An entity details Table's rows open popups of their own
    table: withoutEntityDetailsPopups(element.table),
  });

// An element without following the report's comparison, its date
// comparison or pace table's baseline included
const withoutElementReportComparison = (
  element: Studio.CompositeElement,
): Studio.CompositeElement => {
  const without = withoutReportComparison(element);
  return withoutUndefined({
    ...without,
    table: withoutReportBaselines(without.table),
  });
};

// A Composite's block and element dates (and on a report, its elements
// without popups or reminders, which only a dashboard can show), for the
// content type
export const compositeForContentType = ({
  composite,
  contentType,
}: {
  composite: Studio.CompositeContent;
  contentType: Studio.ContentType;
}): Studio.CompositeContent =>
  withoutUndefined({
    ...composite,
    dateSettings: forContentType({
      dateSettings: composite.dateSettings,
      contentType,
    }),
    rows: composite.rows
      ?.map((row) => ({
        ...row,
        elements:
          contentType === 'report'
            ? row.elements.filter((element) => !isRemindersTable(element))
            : row.elements,
      }))
      .filter((row) => row.elements.length > 0)
      .map((row) => ({
        ...row,
        elements: row.elements.map((element) =>
          withoutUndefined({
            // Only a report has a comparison to follow
            ...withCurrentVisType(
              contentType === 'report'
                ? withoutElementDrillDowns(element)
                : withoutElementReportComparison(element),
            ),
            dateSettings: forContentType({
              dateSettings: element.dateSettings,
              contentType,
            }),
          }),
        ),
      })),
  });
